import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { checkNever } from '@infinex-private/ts-utils/assert';
import type { AssetGroupId } from '@infinex/asset-config';
import {
  isChainAddressEqual,
  makeCacheKey,
  normalizeNativeEvmAssetAddress,
  USDC_BASE_ADDRESS,
  USDC_ETHEREUM_ADDRESS,
} from '@infinex/asset-config';
import { isChainKey, type ChainKey } from '@infinex/chain-config';
import type { Custody } from '@infinex/types';
import { usePlatformFeatureFlagEnabled } from '@infinex/user';

import {
  getTRPCClient,
  useAccountAddresses,
  useEscrowReceivedActivityQuery,
  useEscrowSentActivityQuery,
  useExternalActions,
  useInxActionHistory,
  useAssetMetadataV2BatchQuery,
  useSonarSalePurchases,
  useSwidgeListQuery,
  useWithdrawalsQuery,
} from '../../../api';
import { useGasActivities } from '../../../api/gas/useGasActivities';
import { useFilteredAirdropCampaignAllocations } from '../../../hooks/airdrops';
import { useRaffleWinActivities } from '../../../lib/crates';
import type { Activity, AirdropAllocationWithKey } from '../../../types';
import { ActivityType } from '../../../types';
import { isMainSurfaceSwidgeOrder } from '../../../utils/confidentialOrders';
import {
  dedupeAssetMetadataLookups,
  getActivityAddresses,
  getAssetIdFromMetadata,
  isEscrowFundingWithdrawal,
  mapCashbackClaimToAirdropActivity,
  mapFromPatronReceiptToPatronPurchaseActivity,
  mapGasActivity,
  mapInxActivityToActivity,
  mapSonarPurchaseToActivity,
  mapToAirdropActivity,
  mapToExternalActionActivity,
  mapToPrivateReceiveActivity,
  mapToPrivateSendActivity,
  mapToSendActivity,
  mapToSwidgeActivity,
} from '../utils';

export const DEFAULT_REFETCH_QUERY_CONFIG = {
  refetchInterval: 30 * 1000, // 30 seconds
  staleTime: 30 * 1000, // 30 seconds
  refetchOnMount: true,
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
} as const;

/**
 * Filters for activity log
 */
interface ActivityLogFilters {
  /**
   * Filter by specific wallet IDs. Use 'vault' as the ID for vault custody.
   * When provided, filters activities to only those associated with the specified wallets.
   * @example ['wallet:123', 'vault']
   */
  walletViews?: ('vault' | `wallet:${string}`)[];

  /**
   * Filter by specific custody types.
   * @example ['wallet', 'vault']
   */
  custody?: Custody[];
  /**
   * Filter by activity types. If not provided, shows all activity types.
   * @example [ActivityType.SEND, ActivityType.SWIDGE]
   */
  activityTypes?: ActivityType[];
  /**
   * Filter by asset
   */
  assetFilter?: AssetGroupId;
  /**
   * Filter by chains
   */
  chains?: ChainKey[];
  /**
   * Limit number of results
   */
  limit?: number;
}

export interface UseActivityLogResult {
  activities: Activity[];
  isLoading: boolean;
  hasUserMadeAnyActivity: boolean;
}

export function useActivityLog(
  filters: ActivityLogFilters = {}
): UseActivityLogResult {
  const {
    custody,
    walletViews,
    activityTypes,
    assetFilter,
    chains: chainKeys,
    limit,
  } = filters;

  const allAddresses = useAccountAddresses();
  const hasAccountFilter =
    (walletViews?.length ?? 0) > 0 || (custody?.length ?? 0) > 0;
  const relevantAddressesSet = useMemo(() => {
    if (!hasAccountFilter) {
      return new Set(allAddresses.map((addr) => addr.address));
    }
    return new Set(
      allAddresses
        .filter((addr) => {
          if (custody && custody.includes(addr.custody)) {
            return true;
          }

          if (walletViews) {
            if (walletViews.includes('vault') && addr.custody === 'vault') {
              return true;
            }
            // Multisig safes are addressed as `wallet:<id>` too — their
            // chain addresses carry 'multi-sig' custody, not 'wallet'.
            if (
              walletViews.includes(`wallet:${addr.walletId}`) &&
              (addr.custody === 'wallet' || addr.custody === 'multi-sig')
            ) {
              return true;
            }
          }

          return false;
        })
        .map((addr) => addr.address)
    );
  }, [hasAccountFilter, allAddresses, custody, walletViews]);

  const isCashbackClaimsEnabled = usePlatformFeatureFlagEnabled(
    'enableHlCashbackClaims'
  );

  //-------------------------------------
  // Data Fetching
  //-------------------------------------

  // Note this currently only fetches the first page of swidge
  const {
    data: { orders: allSwidgeOrders } = { orders: [] },
    isSuccess: areSwidgeOrdersFetched,
  } = useSwidgeListQuery(DEFAULT_REFETCH_QUERY_CONFIG);

  // Confidential-INTERNAL ops (private swap/send) belong only to the
  // confidential app's activity; the main feed keeps the boundary moves
  // (shield/deposit/unshield — "Moved to/from Confidential Wallet").
  const swidges = useMemo(
    () => allSwidgeOrders.filter(isMainSurfaceSwidgeOrder),
    [allSwidgeOrders]
  );

  const { raffleWinActivities, isLoading: raffleWinActivitiesLoading } =
    useRaffleWinActivities();
  const raffleWinActivitiesFetched = !raffleWinActivitiesLoading;

  const { data: withdrawals, isSuccess: withdrawalsFetched } =
    useWithdrawalsQuery(DEFAULT_REFETCH_QUERY_CONFIG);

  const { data: cashbackClaims, isSuccess: cashbackClaimsFetched } = useQuery(
    getTRPCClient().hyperliquid.getCashbackClaims.queryOptions(undefined, {
      ...DEFAULT_REFETCH_QUERY_CONFIG,
      enabled: !!isCashbackClaimsEnabled,
    })
  );

  const { data: externalActionsData, isSuccess: externalActionsFetched } =
    useExternalActions(undefined, undefined, DEFAULT_REFETCH_QUERY_CONFIG);

  // Extract actions array from the response object
  const externalActions = useMemo(
    () => externalActionsData?.actions ?? [],
    [externalActionsData?.actions]
  );
  const {
    data: { activities: gasActivities = [] } = {},
    isSuccess: gasActivitiesFetched,
  } = useGasActivities();

  const {
    data: { receipts: patronReceipts } = { receipts: [] },
    isSuccess: patronReceiptsFetched,
  } = useQuery(
    getTRPCClient().patron.getReceipts.queryOptions(
      undefined,
      DEFAULT_REFETCH_QUERY_CONFIG
    )
  );

  const { data: airdropCampaignsAllocations, status: airdropsStatus } =
    useFilteredAirdropCampaignAllocations({
      claimStatus: ['started', 'error', 'claimed'],
    });
  const airdropsFetched = airdropsStatus === 'loaded';
  const airdropAllocations = useMemo((): AirdropAllocationWithKey[] => {
    return (
      airdropCampaignsAllocations?.flatMap((campaignAllocations) =>
        [
          ...campaignAllocations.unclaimedAllocationSets,
          ...campaignAllocations.claimedAllocationSets,
        ].flatMap(({ allocations, decimals }) =>
          allocations.map((allocation) => ({
            ...allocation,
            decimals,
            key: campaignAllocations.campaign.key,
            title: campaignAllocations.campaign.title,
          }))
        )
      ) ?? []
    );
  }, [airdropCampaignsAllocations]);

  // Private (escrow) sends — sender side + claimed receives. Settled
  // (success or error) so a missing/failing escrowSend route degrades to an
  // empty contribution instead of wedging the whole activity log.
  const {
    data: escrowSentItems,
    isFetched: escrowSentFetched,
    isError: escrowSentErrored,
  } = useEscrowSentActivityQuery(DEFAULT_REFETCH_QUERY_CONFIG);
  const {
    data: escrowReceivedItems,
    isFetched: escrowReceivedFetched,
    isError: escrowReceivedErrored,
  } = useEscrowReceivedActivityQuery(DEFAULT_REFETCH_QUERY_CONFIG);
  const escrowSentSettled = escrowSentFetched || escrowSentErrored;
  const escrowReceivedSettled = escrowReceivedFetched || escrowReceivedErrored;

  const activityAssetMetadataLookups = useMemo(
    () =>
      dedupeAssetMetadataLookups([
        ...(withdrawals ?? []).map((withdrawal) => ({
          chain: withdrawal.chain,
          address: withdrawal.tokenAddress,
        })),
        // `usd_class_transfer` records (USDC, no chain/assetAddress) have no
        // per-asset metadata to fetch, so they're excluded from the lookups.
        ...externalActions.flatMap((action) =>
          'chain' in action
            ? [{ chain: action.chain, address: action.assetAddress }]
            : []
        ),
        ...swidges.flatMap((swidge) => [
          {
            chain: swidge.quote.from.chain,
            address: normalizeNativeEvmAssetAddress(
              swidge.quote.from.assetAddress
            ),
          },
          {
            chain: swidge.quote.to.chain,
            address: normalizeNativeEvmAssetAddress(
              swidge.quote.to.assetAddress
            ),
          },
        ]),
        // Private (escrow) rows carry only chain + assetAddress on the wire;
        // batching their metadata lets asset filters resolve the canonical
        // assetId for these rows.
        ...[...(escrowSentItems ?? []), ...(escrowReceivedItems ?? [])].flatMap(
          (item) =>
            isChainKey(item.chainKey)
              ? [{ chain: item.chainKey, address: item.assetId }]
              : []
        ),
      ]),
    [
      withdrawals,
      externalActions,
      swidges,
      escrowSentItems,
      escrowReceivedItems,
    ]
  );

  const {
    data: activityMetadataByCacheKey,
    isFetching: isActivityMetadataFetching,
  } = useAssetMetadataV2BatchQuery(activityAssetMetadataLookups);

  const isActivityMetadataMissing = useMemo(
    () =>
      activityAssetMetadataLookups.some(
        (lookup) =>
          !activityMetadataByCacheKey ||
          !(makeCacheKey(lookup) in activityMetadataByCacheKey)
      ),
    [activityAssetMetadataLookups, activityMetadataByCacheKey]
  );

  const isActivityMetadataLoading =
    isActivityMetadataFetching && isActivityMetadataMissing;

  // Sonar sale purchases from activity.getSonarSalePurchases
  const {
    data: sonarSalePurchasesData,
    isFetched: sonarSalePurchasesFetched,
    isError: sonarSalePurchasesErrored,
  } = useSonarSalePurchases(DEFAULT_REFETCH_QUERY_CONFIG);
  // Settled (success or error) so a failing query degrades to an empty sonar
  // section instead of wedging the whole activity log.
  const sonarSalePurchasesSettled =
    sonarSalePurchasesFetched || sonarSalePurchasesErrored;
  const sonarPurchases = useMemo(
    () => sonarSalePurchasesData?.purchases ?? [],
    [sonarSalePurchasesData?.purchases]
  );

  // NOTE: Sonar sale purchases are always made from the user's main Infinex wallet on Base.
  // The endpoint doesn't include wallet addresses, so we derive it from account addresses
  // to enable proper wallet-based filtering in the activity log.
  const mainWalletAddressOnBase = useMemo(() => {
    const baseWalletAddress = allAddresses.find(
      (addr) => addr.custody === 'wallet' && addr.chain === 'base'
    );
    return baseWalletAddress?.address ?? '';
  }, [allAddresses]);

  // INX action history (claims, early unlocks, voucher redemptions)
  const { data: inxActionHistory, isSuccess: inxActionHistoryFetched } =
    useInxActionHistory(DEFAULT_REFETCH_QUERY_CONFIG);
  const inxActivities = useMemo(
    () => inxActionHistory?.items ?? [],
    [inxActionHistory?.items]
  );

  const isLoading =
    isActivityMetadataLoading ||
    !gasActivitiesFetched ||
    !airdropsFetched ||
    !withdrawalsFetched ||
    !externalActionsFetched ||
    !patronReceiptsFetched ||
    !areSwidgeOrdersFetched ||
    !raffleWinActivitiesFetched ||
    !sonarSalePurchasesSettled ||
    !inxActionHistoryFetched ||
    !escrowSentSettled ||
    !escrowReceivedSettled ||
    (!!isCashbackClaimsEnabled && !cashbackClaimsFetched);

  //-------------------------------------
  // Activity Checks
  //-------------------------------------
  const hasUserMadeAnyActivity = useMemo(() => {
    return (
      (withdrawals ?? []).length > 0 ||
      airdropAllocations.length > 0 ||
      gasActivities.length > 0 ||
      swidges.length > 0 ||
      externalActions.length > 0 ||
      (raffleWinActivities ?? []).length > 0 ||
      sonarPurchases.length > 0 ||
      inxActivities.length > 0 ||
      (escrowSentItems ?? []).length > 0 ||
      (escrowReceivedItems ?? []).length > 0 ||
      ((cashbackClaims?.cashbackClaims ?? []).length > 0 &&
        !!isCashbackClaimsEnabled)
    );
  }, [
    withdrawals,
    airdropAllocations,
    gasActivities,
    swidges,
    externalActions,
    raffleWinActivities,
    sonarPurchases,
    inxActivities,
    escrowSentItems,
    escrowReceivedItems,
    cashbackClaims,
    isCashbackClaimsEnabled,
  ]);

  //-------------------------------------
  // Activity Transformation
  //-------------------------------------
  const activities = useMemo(() => {
    if (isLoading) return [];

    // Helper: Check if an address belongs to the filtered wallets
    const isAddressInFilter = (address: string): boolean => {
      return relevantAddressesSet.has(address);
    };

    // Helper: Check if activity should be included based on wallet filter
    const shouldIncludeActivityByWallet = (activity: Activity): boolean => {
      if (!hasAccountFilter) return true;
      const activityAddresses = getActivityAddresses(activity);
      return activityAddresses.some((address) => isAddressInFilter(address));
    };

    const filterByActivityType = (activity: Activity): boolean => {
      if (!activityTypes || activityTypes.length === 0) return true;
      return activityTypes.includes(activity.type);
    };

    const filterByChain = (activity: Activity): boolean => {
      if (!chainKeys) return true;
      if (
        activity.type === ActivityType.PATRON_PURCHASE &&
        chainKeys.includes('ethereum')
      ) {
        return true;
      }
      return (
        'chain' in activity &&
        !!activity.chain &&
        chainKeys.includes(activity.chain)
      );
    };

    const filterByAsset = (activity: Activity): boolean => {
      if (!assetFilter) return true;

      switch (activity.type) {
        case ActivityType.SEND:
          return (
            ('assetId' in assetFilter &&
              activity.assetId === assetFilter.assetId) ||
            ('address' in assetFilter &&
              'chain' in assetFilter &&
              activity.assetAddress === assetFilter.address &&
              activity.chain === assetFilter.chain)
          );
        case ActivityType.AIRDROP:
        case ActivityType.AIRDROP_CLAIM:
        case ActivityType.PATRON_PURCHASE:
        case ActivityType.PATRON_REDEMPTION:
          return (
            'assetId' in assetFilter && activity.assetId === assetFilter.assetId
          );

        case ActivityType.SWIDGE:
          // For Swidge activities, check both source and destination assets
          if ('assetId' in assetFilter) {
            return (
              activity.fromAssetId === assetFilter.assetId ||
              activity.toAssetId === assetFilter.assetId
            );
          } else if ('address' in assetFilter && 'chain' in assetFilter) {
            return (
              (activity.fromAssetAddress === assetFilter.address &&
                activity.fromChain === assetFilter.chain) ||
              (activity.toAssetAddress === assetFilter.address &&
                activity.toChain === assetFilter.chain)
            );
          }
          return false;

        case ActivityType.EXTERNAL_APP_ACTION:
          // For external actions, check the asset
          if ('assetId' in assetFilter) {
            return activity.assetId === assetFilter.assetId;
          } else if ('address' in assetFilter && 'chain' in assetFilter) {
            return (
              activity.assetAddress === assetFilter.address &&
              activity.chain === assetFilter.chain
            );
          }
          return false;

        case ActivityType.GAS:
          // Gas activities are presented as USDC (the Gas Account is an
          // off-chain USD ledger), so they match USDC filters only
          if ('assetId' in assetFilter) {
            return assetFilter.assetId === 'usd_coin';
          }
          if ('address' in assetFilter && 'chain' in assetFilter) {
            return (
              isChainAddressEqual(assetFilter.address, USDC_BASE_ADDRESS) &&
              assetFilter.chain === 'base'
            );
          }
          return false;

        case ActivityType.CASHBACK_CLAIM:
          return false;

        case ActivityType.PRIZE_DRAW_WIN:
          return false;

        case ActivityType.SONAR_SALE_PURCHASE:
          // Sonar sale purchases are always USDC on Base
          // Handle assetId format (e.g., { assetId: 'usd_coin' })
          if ('assetId' in assetFilter) {
            return assetFilter.assetId === 'usd_coin';
          }
          // Handle chain/address format
          if ('address' in assetFilter && 'chain' in assetFilter) {
            return (
              assetFilter.address.toLowerCase() === USDC_BASE_ADDRESS &&
              assetFilter.chain === 'base'
            );
          }
          return false;

        // INX activities - INX token on Ethereum
        case ActivityType.INX_CLAIM:
          // INX activities involve INX token; filter passes if user is filtering by INX
          if ('assetId' in assetFilter) {
            return assetFilter.assetId === 'infinex';
          }

          return false;

        case ActivityType.INX_EARLY_UNLOCK:
        case ActivityType.INX_VOUCHER_REDEMPTION:
          if ('assetId' in assetFilter) {
            return (
              assetFilter.assetId === 'infinex' ||
              assetFilter.assetId === 'usd_coin'
            );
          }
          if ('address' in assetFilter && 'chain' in assetFilter) {
            return (
              assetFilter.address.toLowerCase() === USDC_ETHEREUM_ADDRESS &&
              assetFilter.chain === 'ethereum'
            );
          }
          return false;

        case ActivityType.PRIVATE_SEND:
        case ActivityType.PRIVATE_RECEIVE: {
          // Private rows carry only chain + assetAddress; resolve the
          // canonical assetId from the batched metadata so canonical
          // (grouped-asset) filters also match.
          const resolvedAssetId = getAssetIdFromMetadata(
            activityMetadataByCacheKey?.[
              makeCacheKey({
                chain: activity.chain,
                address: activity.assetAddress,
              })
            ]
          );
          return (
            ('assetId' in assetFilter &&
              resolvedAssetId === assetFilter.assetId) ||
            ('address' in assetFilter &&
              'chain' in assetFilter &&
              isChainAddressEqual(activity.assetAddress, assetFilter.address) &&
              activity.chain === assetFilter.chain)
          );
        }

        default:
          checkNever(activity);
          return false;
      }
    };

    // Wallet-funded escrow sends double as raw transfers (requestId =
    // escrowSendId); keep only the private-send rows.
    const escrowSendIds = new Set(
      (escrowSentItems ?? []).map((item) => item.id)
    );

    const filteredRows = [
      // Withdrawals - shared between wallet and vault
      ...withdrawals
        .filter((w) => !isEscrowFundingWithdrawal(w, escrowSendIds))
        .flatMap((w) => {
          const lookup = { chain: w.chain, address: w.tokenAddress };
          return mapToSendActivity(
            w,
            activityMetadataByCacheKey?.[makeCacheKey(lookup)]
          );
        }),

      // External actions (Hyperliquid deposits/withdrawals) - shared
      ...externalActions.map((action) => {
        // `usd_class_transfer` records carry no chain/assetAddress; the mapper
        // defaults to USDC (6 decimals) when no asset metadata is supplied.
        const lookup =
          'chain' in action
            ? { chain: action.chain, address: action.assetAddress }
            : undefined;
        return mapToExternalActionActivity(
          action,
          lookup
            ? activityMetadataByCacheKey?.[makeCacheKey(lookup)]
            : undefined
        );
      }),

      // Wallet activities (swidges, gas)
      ...swidges.map((c) => {
        const fromLookup = {
          chain: c.quote.from.chain,
          address: normalizeNativeEvmAssetAddress(c.quote.from.assetAddress),
        };
        const toLookup = {
          chain: c.quote.to.chain,
          address: normalizeNativeEvmAssetAddress(c.quote.to.assetAddress),
        };
        return mapToSwidgeActivity(c, {
          fromAssetMetadata:
            activityMetadataByCacheKey?.[makeCacheKey(fromLookup)],
          toAssetMetadata: activityMetadataByCacheKey?.[makeCacheKey(toLookup)],
        });
      }),
      ...gasActivities.map(mapGasActivity),

      // Cashback claims - account-level activity
      ...(isCashbackClaimsEnabled &&
      cashbackClaims &&
      cashbackClaims?.cashbackClaims.length > 0
        ? cashbackClaims.cashbackClaims.map((c) =>
            mapCashbackClaimToAirdropActivity(c)
          )
        : []),

      // Airdrops - can be wallet, vault, or account-level
      ...airdropAllocations.flatMap((a) => {
        return mapToAirdropActivity(a);
      }),

      // Vault activities (patron purchases)
      ...patronReceipts.map((r) =>
        mapFromPatronReceiptToPatronPurchaseActivity(r)
      ),

      // Raffle win activities
      ...(raffleWinActivities ?? []),

      // Sonar sale purchases (always from main wallet on Base)
      ...sonarPurchases.map((p) =>
        mapSonarPurchaseToActivity(p, mainWalletAddressOnBase)
      ),

      // INX activities (claims, early unlocks, voucher redemptions)
      ...inxActivities.map(mapInxActivityToActivity),

      // Private sends / receives (escrow sends)
      ...(escrowSentItems ?? []).flatMap(
        (item) => mapToPrivateSendActivity(item) ?? []
      ),
      ...(escrowReceivedItems ?? []).flatMap(
        (item) => mapToPrivateReceiveActivity(item) ?? []
      ),
    ]
      .filter(filterByActivityType)
      .filter(filterByChain)
      .filter(shouldIncludeActivityByWallet)
      .filter(filterByAsset)
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

    return limit ? filteredRows.slice(0, limit) : filteredRows;
  }, [
    isLoading,
    withdrawals,
    externalActions,
    swidges,
    gasActivities,
    isCashbackClaimsEnabled,
    cashbackClaims,
    airdropAllocations,
    patronReceipts,
    raffleWinActivities,
    sonarPurchases,
    inxActivities,
    escrowSentItems,
    escrowReceivedItems,
    mainWalletAddressOnBase,
    limit,
    relevantAddressesSet,
    hasAccountFilter,
    activityTypes,
    chainKeys,
    assetFilter,
    activityMetadataByCacheKey,
  ]);

  return {
    activities,
    isLoading,
    hasUserMadeAnyActivity,
  };
}
